import React, { useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { Box, TextField } from 'edenred-ui';
import { useTranslation } from 'react-i18next';
import { isToday, isBefore, isAfter } from 'date-fns';

import { createRules, FormFields } from '@epi-forms/helpers';
import { EmployeeTypeModes } from '@epi-constants/beneficiary';
import { IEmployeeTypeDataField } from '@epi-models/pages/Beneficiaries';
import { onHoldPending, required } from '@epi-helpers/formHelpers/validators';
import { TODAY } from '@epi-constants/downloadReports';
import { useGetDetailsQuery } from '@epi-api/user';

export const EmployeeTypeDataField = ({
  isAddMode = false
}: IEmployeeTypeDataField) => {
  const { t } = useTranslation();
  const { control, watch, setValue, clearErrors, trigger } = useFormContext();
  const [prevValue, setPrevValue] = useState('');
  const savedPercentageRef = useRef('');
  const isEditMode = watch(FormFields.EditMode);
  const EoB = watch(FormFields.EndOfBenefit);
  const workingHoursPercentage = watch(FormFields.EmployeePercentage);
  const pendingLoadDate = watch(FormFields.PendingLoadDate);
  const percentedField = FormFields.EmployeePercentage;
  const daysForLunari = FormFields.DaysForLunari;
  const readOnly = isAddMode ? false : !isEditMode;
  const validators = [required, onHoldPending(pendingLoadDate)];
  const rules = createRules(validators);
  const { data: customerData } = useGetDetailsQuery(undefined, {
    refetchOnMountOrArgChange: false
  });

  const fullTime = {
    display: t('containers.beneficiaries.detail.FullTime'),
    id: EmployeeTypeModes.FullTime
  };

  const partTime = {
    display: t('containers.beneficiaries.detail.PartTime'),
    id: EmployeeTypeModes.PartTime
  };

  const temporaryEmployed = {
    display: t('containers.beneficiaries.detail.TemporaryEmployed'),
    id: EmployeeTypeModes.TemporaryEmployed
  };

  const onHold = {
    display: t('containers.beneficiaries.detail.Onhold'),
    id: EmployeeTypeModes.Onhold
  };

  const blocked = {
    display: t('containers.beneficiaries.detail.Blocked'),
    id: EmployeeTypeModes.Blocked
  };

  const options = isAddMode
    ? [fullTime, partTime, temporaryEmployed]
    : customerData?.isPbu
    ? [fullTime, partTime, temporaryEmployed, onHold, blocked]
    : [fullTime, partTime, temporaryEmployed, onHold];

  const {
    field: { onChange, value },
    fieldState: { error, isDirty }
  } = useController({
    name: FormFields.EmployeeType,
    control,
    rules
  });
  const isDropped = value === EmployeeTypeModes.Dropped;
  const isPartTime = value === EmployeeTypeModes.PartTime;

  const setWorkingHoursAsPercentage = (
    employmentType: keyof typeof EmployeeTypeModes
  ) => {
    if (employmentType === EmployeeTypeModes.FullTime) {
      setValue(percentedField, '100', {
        shouldValidate: true
      });
      return;
    }

    if (
      [
        EmployeeTypeModes.Onhold,
        EmployeeTypeModes.Dropped,
        EmployeeTypeModes.Blocked
      ].includes(employmentType)
    ) {
      return setValue(percentedField, workingHoursPercentage);
    }

    if (isPartTime || employmentType === EmployeeTypeModes.TemporaryEmployed) {
      if (isDirty) {
        setValue(percentedField, '', { shouldValidate: true });
        trigger(percentedField, { shouldFocus: true });
      } else {
        setValue(percentedField, workingHoursPercentage);
        clearErrors(percentedField);
      }
      return;
    }

    return setValue(percentedField, '');
  };

  const handleOnChange = e => {
    setValue(FormFields.EditingType, true);
    setWorkingHoursAsPercentage(e.target.value);
    onChange(e);
  };

  useEffect(() => {
    if (!isAddMode) {
      if (!EoB && isDropped) {
        setPrevValue('');
        setValue(daysForLunari, '');
        setValue(percentedField, '');
        return setValue(FormFields.EmployeeType, '');
      }
      if (
        prevValue === EmployeeTypeModes.Dropped &&
        isAfter(new Date(EoB), TODAY)
      ) {
        setPrevValue('');
        setValue(daysForLunari, '');
        setValue(percentedField, '');
        return setValue(FormFields.EmployeeType, '');
      }

      if (isToday(new Date(EoB)) || isBefore(new Date(EoB), TODAY)) {
        if (workingHoursPercentage) {
          savedPercentageRef.current = workingHoursPercentage;
        }
        setValue(FormFields.EmployeeType, EmployeeTypeModes.Dropped);
        setValue(percentedField, savedPercentageRef.current);
        return setPrevValue(EmployeeTypeModes.Dropped);
      }

      setPrevValue('');
      setValue(FormFields.EmployeeType, value);
    }
    return setValue(FormFields.EmployeeType, value || '');
  }, [EoB, isAddMode]);

  return (
    <>
      <TextField
        label={t('containers.beneficiaries.detail.employee_type')}
        select={!isDropped}
        required
        value={
          isDropped
            ? t('containers.beneficiaries.detail.Dropped')
            : EmployeeTypeModes[value] ?? ''
        }
        readOnly={!isDropped ? readOnly : true}
        id={'employee-type-select'}
        onChange={handleOnChange}
        error={!!error}
        helperText={error?.message}
        sx={{
          '& .MuiSelect-select:focus': { backgroundColor: 'transparent' }
        }}
      >
        {options.map(option => (
          <TextField.Item key={option.id} value={option.id}>
            {option.display}
          </TextField.Item>
        ))}
      </TextField>
      {value === EmployeeTypeModes.Onhold && !error && (
        <Box id="edit_beneficiary_employee_type_subtext">
          {t('containers.beneficiaries.detail.employee_type_subtext')}
        </Box>
      )}
    </>
  );
};
